import { useCallback, useEffect, useState } from "react";
import { getDefaultBranch } from "../../Api/ServerInterface";
import useStore from "../../Store/Store";
import { openDirectory } from "../../utils/LocalFileController/LocalFileController";
import { logger } from "../../utils/Logger";
import { removeURLParam, setURLParam, getURLParam } from "../../utils/URLUtils";
import "./LandingComponent.css";
import { RecentFilesDropdown } from "./RecentFilesDropdown";
import { storeOpenProjectMetadataInCodeCanvasIndexedDB } from "../../utils/CodeCanvasIndexedDB";
import { Typography } from "@mui/material";
import { theme } from "../../Themes/Themes";
import VideoTutorial from "../VideoTutorial/VideoTutorial";
import LinkIcon from "@mui/icons-material/Link";
import {
  AccountTree,
  DriveFolderUpload,
  GitHub,
  HighlightOff,
  PlayArrow,
  School,
} from "@mui/icons-material";
import { login } from "../../utils/AuthUtils";
import FirebaseAuth from "../Common/FirebaseAuth/FirebaseAuth";

/**
 * Proactively checks if a GitHub repository is publicly accessible
 * before making any server calls to get repository details.
 *
 * @param {string} owner - Repository owner
 * @param {string} repo - Repository name
 * @returns {Promise<boolean>} - True if publicly accessible, false if private/non-existent
 */
const checkRepositoryAccessibility = async (owner, repo) => {
  try {
    const response = await fetch(
      `https://api.github.com/repos/${owner}/${repo}`,
      {
        method: "GET",
        headers: {
          Accept: "application/vnd.github.v3+json",
        },
      }
    );

    // If we get a 200 response, the repository is publicly accessible
    return response.status === 200;
  } catch (error) {
    // Network error or other issues - assume not accessible
    logger.error("Error checking repository accessibility:", error);
    return false;
  }
};

export const loadPublicRepo = (publicURL, isUserAuthenticated = false) => {
  return new Promise(async (resolve, reject) => {
    if (publicURL && publicURL.length > 0) {
      const state = useStore.getState();
      // remove existing URL params from address bar
      try {
        // Try to parse the URL as a GitHub URL
        // If it's not a valid GitHub URL, the backend will handle it
        const urlParts = publicURL.split("/");
        let repoName, userName, branch;

        // Check if it looks like a GitHub URL
        if (publicURL.includes("github.com")) {
          repoName = urlParts[4];
          userName = urlParts[3];
          branch = urlParts[6];
        } else {
          // Try to extract from the URL path anyway
          // This might be a CodeCanvas URL or another format
          // Let the backend determine if it's valid
          const pathParts = new URL(publicURL).pathname
            .split("/")
            .filter((p) => p);
          if (pathParts.length >= 2) {
            userName = pathParts[0];
            repoName = pathParts[1];
            branch = pathParts[3]; // if it follows /owner/repo/tree/branch pattern
          }
        }

        if (!repoName || !userName) {
          state.setErrorNotification(`Invalid URL format`);
          reject(new Error("Invalid URL"));
          return;
        }

        // Only perform proactive check if user is NOT authenticated
        // If user is authenticated, let the normal flow handle private repositories
        if (!isUserAuthenticated && publicURL.includes("github.com")) {
          // Proactively check if repository is publicly accessible
          const isAccessible = await checkRepositoryAccessibility(
            userName,
            repoName
          );

          if (!isAccessible) {
            logger.debug.info(
              "Repository is not publicly accessible and user is not authenticated, treating as private:",
              { userName, repoName }
            );
            reject(new Error("PRIVATE_REPOSITORY"));
            return;
          }

          logger.debug.info(
            "Repository is publicly accessible, proceeding with loading:",
            { userName, repoName }
          );
        } else {
          logger.debug.info(
            "User is authenticated or non-GitHub URL, proceeding with normal loading:",
            { userName, repoName, isUserAuthenticated }
          );
        }

        // Preserve model parameter before removing URL params
        const existingModel = getURLParam(window, "model");

        // Remove existing URL params from address bar
        ["repo", "branch", "owner"].forEach((param) => {
          removeURLParam(window, param);
        });

        state.setUsername(userName);
        state.setReposList([
          ...state.reposList,
          { name: repoName, owner: { login: userName } },
        ]);

        // Set URL Params
        // Don't set session here - let the calling code determine the session type
        setURLParam(window, "repo", repoName);
        setURLParam(window, "owner", userName);

        // Restore model parameter if it existed
        if (existingModel) {
          setURLParam(window, "model", existingModel);
          state.setSelectedModel(existingModel);
        }

        state.setCurrentRepo(repoName, { owner: userName });

        // Clear any existing error notifications since we're successfully setting up the repo
        state.setErrorNotification("");

        if (branch) {
          setURLParam(window, "branch", branch);
          state.setCurrentBranch(branch);
          resolve({ repoName, userName, branch });
        } else {
          // get default branch from github
          getDefaultBranch(repoName, userName)
            .then((defaultBranch) => {
              if (defaultBranch) {
                let branch = defaultBranch as unknown as string;
                setURLParam(window, "branch", branch);
                state.setBranchesList([{ name: branch }]);
                state.setCurrentBranch(branch);
                resolve({ repoName, userName, branch });
              } else {
                // If no default branch is returned, treat as private repository
                reject(new Error("PRIVATE_REPOSITORY"));
              }
            })
            .catch((error) => {
              // Any error accessing the repository suggests it might be private
              logger.error("Repository access error:", error);

              // Check if it's a specific error that indicates bad credentials
              if (
                error.message === "GITHUB_BAD_CREDENTIALS" ||
                error.message === "Bad credentials" ||
                error.message === "REPO_NOT_FOUND"
              ) {
                // These errors suggest we need to authenticate
                reject(new Error("PRIVATE_REPOSITORY"));
              } else {
                // For other errors, still treat as private repository to trigger auth flow
                reject(new Error("PRIVATE_REPOSITORY"));
              }
            });
        }
        // store repoURL in indexedDB
        storeOpenProjectMetadataInCodeCanvasIndexedDB();
      } catch (e) {
        logger.error(e);
        state.setErrorNotification(`Failed to retrieve repo from URL`);
        reject(e);
      }
    } else {
      reject(new Error("Empty URL"));
    }
  });
};

const LandingComponent = () => {
  const {
    isLoggedIn,
    directoryHandle,
    sessionMode,
    isUserChoosingDirectory,
    showLandingComponent,
    currentBranch,
    setShowLandingComponent,
    currentRepo,
    privateRepositoryPrompt,
    setPrivateRepositoryPrompt,
  } = useStore((state) => ({
    isLoggedIn: state.session.isLoggedIn,
    directoryHandle: state.session.local.directory.handle,
    sessionMode: state.session.mode,
    isUserChoosingDirectory: state.isUserChoosingDirectory,
    showLandingComponent: state.showLandingComponent,
    currentBranch: state.currentBranch,
    setShowLandingComponent: state.setShowLandingComponent,
    currentRepo: state.currentRepo,
    privateRepositoryPrompt: state.privateRepositoryPrompt,
    setPrivateRepositoryPrompt: state.setPrivateRepositoryPrompt,
  }));

  const handleLogin = useCallback(async () => {
    if (!isLoggedIn) {
      // Clear private repository prompt when user attempts to login
      if (privateRepositoryPrompt) {
        setPrivateRepositoryPrompt(false);
      }
      login();
      return;
    }
  }, [isLoggedIn, privateRepositoryPrompt, setPrivateRepositoryPrompt]);

  const loadDemo = () => {
    const currentDomain = window.location.origin;
    const demoURL = `${currentDomain}/?session=unauthenticatedGithub&repo=react-ecommerce&owner=Abdulnaser97&branch=master`;
    setShowLandingComponent(false);
    window.location.assign(demoURL);
  };

  const goNotion = () => {
    window.location.assign(
      `https://pie-crepe-38f.notion.site/Documentation-cf20cbdf134247cb93bf6366d9055076`
    );
  };

  const handleOpenFolder = async () => {
    try {
      await openDirectory(true);
      setShowLandingComponent(false);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    // Always show landing component when private repository prompt is active
    if (privateRepositoryPrompt) {
      setShowLandingComponent(true);
      return;
    }

    if (
      (sessionMode === "unauthenticatedGithub" && !currentRepo) ||
      (sessionMode === "local" && !directoryHandle && !isUserChoosingDirectory)
    ) {
      setShowLandingComponent(true);
    } else {
      setShowLandingComponent(false);
      // Clear private repository prompt when successfully loading a repository
      if (privateRepositoryPrompt) {
        setPrivateRepositoryPrompt(false);
      }
    }
  }, [
    directoryHandle,
    sessionMode,
    isUserChoosingDirectory,
    setShowLandingComponent,
    currentRepo,
    privateRepositoryPrompt,
    setPrivateRepositoryPrompt,
  ]);

  const [publicURL, setPublicURL] = useState("");
  const handlePublicURLChange = (e) => {
    setPublicURL(e.target.value);
  };

  const handleEnterKey = (e) => {
    if (e.key === "Enter") {
      handleLoadRepo();
    }
  };

  const handleLoadRepo = () => {
    if (publicURL.trim()) {
      // Clear any existing private repository prompt
      if (privateRepositoryPrompt) {
        setPrivateRepositoryPrompt(false);
      }

      try {
        // Parse the GitHub URL to extract owner and repo
        const githubUrl = publicURL.trim();
        let url: URL;

        try {
          url = new URL(githubUrl);
        } catch (error) {
          throw new Error("Invalid URL format");
        }

        // Check if it's a GitHub URL
        if (!url.hostname.includes("github.com")) {
          throw new Error("Please enter a valid GitHub repository URL");
        }

        // Extract owner and repo from path
        const pathParts = url.pathname.split("/").filter(Boolean);
        if (pathParts.length < 2) {
          throw new Error("Invalid GitHub repository URL format");
        }

        const owner = pathParts[0];
        const repo = pathParts[1];

        // Get current domain and construct CodeCanvas-style URL
        const currentDomain = window.location.origin;
        const codeCanvasUrl = `${currentDomain}/${owner}/${repo}`;

        logger.debug.info(
          `Redirecting from GitHub URL ${githubUrl} to CodeCanvas URL ${codeCanvasUrl}`
        );

        // Clear the input field and hide landing component
        setPublicURL("");
        setShowLandingComponent(false);

        // Redirect to CodeCanvas-style URL (this will trigger the same authentication flow as direct URL navigation)
        window.location.assign(codeCanvasUrl);
      } catch (error) {
        logger.error("Error processing GitHub URL:", error);
        useStore
          .getState()
          .setErrorNotification(
            error.message ||
              "Failed to process repository URL. Please check the URL format and try again."
          );
      }
    }
  };

  const handleShowVideo = () => {
    useStore.getState().setVideoTutorialSection("GETTING_STARTED");
  };
  return (
    <>
      {showLandingComponent && (
        <div className="landing-wrapper">
          {/* Firebase Auth Component - Top Right Corner */}
          <div
            style={{
              position: "absolute",
              top: "10px",
              right: "80px",
              zIndex: 1000,
            }}
          >
            <FirebaseAuth />
          </div>

          <div className="landing-title">
            <h1>CodeCanvas</h1>
          </div>
          <div className="landing-content">
            {(sessionMode === "unauthenticatedGithub" ||
              isLoggedIn ||
              currentBranch) && (
              <div
                className="landingComponentCloseButton"
                onClick={() => setShowLandingComponent(false)}
              >
                <HighlightOff className="landingComponentCloseButtonIcon" />
              </div>
            )}
            <svg
              className="landing-logo"
              viewBox="0 0 517 449"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g filter="url(#a)">
                <path
                  d="M467.347 291.533c-8.52 28.882-38.84 45.388-67.721 36.868-28.882-8.52-45.388-38.84-36.868-67.722 8.52-28.881 38.84-45.387 67.722-36.867 28.881 8.52 45.387 38.84 36.867 67.721Z"
                  fill="#FFAEA6"
                />
                <path
                  d="M451.655 286.919c-5.964 20.217-27.188 31.771-47.405 25.807-20.217-5.964-31.772-27.188-25.808-47.405 5.964-20.217 27.188-31.771 47.405-25.807 20.217 5.964 31.772 27.188 25.808 47.405Z"
                  fill="#FEF3F3"
                />
                <path
                  d="M281.642 111.715c-8.52 28.881-38.84 45.388-67.721 36.868-28.882-8.52-45.388-38.84-36.868-67.722 8.52-28.882 38.84-45.388 67.722-36.868 28.881 8.52 45.388 38.84 36.867 67.722Z"
                  fill="#FFAEA6"
                />
                <path
                  d="M265.958 107.075c-5.964 20.217-27.188 31.771-47.405 25.807-20.217-5.964-31.772-27.188-25.808-47.405 5.964-20.217 27.188-31.771 47.405-25.807 20.218 5.964 31.772 27.188 25.808 47.405Z"
                  fill="#FEF3F3"
                />
                <path
                  d="M123.024 292.582c-8.52 28.882-38.84 45.388-67.722 36.868-28.881-8.52-45.388-38.84-36.868-67.722 8.52-28.881 38.84-45.388 67.722-36.867 28.882 8.52 45.388 38.84 36.868 67.721Z"
                  fill="#FFAEA6"
                />
                <path
                  d="M107.335 287.958c-5.964 20.217-27.188 31.771-47.405 25.807-20.217-5.964-31.771-27.188-25.807-47.405 5.964-20.217 27.188-31.771 47.405-25.807 20.217 5.964 31.771 27.188 25.807 47.405Z"
                  fill="#FEF3F3"
                />
                <path
                  d="M426.353 179.989c-4.686 15.885-21.362 24.964-37.247 20.278-15.885-4.686-24.963-21.362-20.277-37.247 4.686-15.885 21.362-24.963 37.247-20.277 15.885 4.686 24.963 21.362 20.277 37.246Z"
                  fill="#FFAEA6"
                />
                <path
                  d="M415.897 176.897c-2.982 10.108-13.594 15.886-23.703 12.904-10.108-2.982-15.885-13.594-12.903-23.703 2.982-10.108 13.594-15.886 23.702-12.904 10.109 2.982 15.886 13.594 12.904 23.703Z"
                  fill="#FEF3F3"
                />
                <path
                  d="M162.834 167.6c-4.26 14.44-19.42 22.694-33.861 18.434-14.441-4.261-22.694-19.421-18.434-33.861 4.26-14.441 19.42-22.694 33.861-18.434 14.441 4.26 22.694 19.42 18.434 33.861Z"
                  fill="#FFAEA6"
                />
                <path
                  d="M152.37 164.532c-2.556 8.665-11.652 13.617-20.316 11.061-8.665-2.556-13.617-11.652-11.061-20.317 2.556-8.664 11.652-13.616 20.317-11.06 8.664 2.556 13.616 11.652 11.06 20.316Z"
                  fill="#FEF3F3"
                />
                <path
                  d="M288.352 378.017c-4.686 15.885-21.362 24.963-37.247 20.277-15.885-4.686-24.963-21.362-20.277-37.247 4.686-15.885 21.362-24.963 37.247-20.277 15.885 4.686 24.963 21.362 20.277 37.247Z"
                  fill="#FFAEA6"
                />
                <path
                  d="M277.896 374.922c-2.982 10.109-13.594 15.886-23.703 12.904-10.108-2.982-15.886-13.594-12.904-23.703 2.982-10.108 13.594-15.885 23.703-12.903 10.109 2.982 15.886 13.594 12.904 23.702Z"
                  fill="#FEF3F3"
                />
                <path
                  d="M299.23 244.799c-6.39 21.661-29.13 34.041-50.792 27.651-21.661-6.39-34.04-29.13-27.65-50.792 6.39-21.661 29.13-34.04 50.791-27.65 21.661 6.39 34.041 29.13 27.651 50.791Z"
                  fill="#FFAEA6"
                />
                <path
                  fill="#FFAEA6"
                  d="m379.432 186.473-82.856 36.776-4.026-9.071 82.856-36.776zM296.317 235.396l83.023 24.492-4.472 15.159-83.023-24.492zM244.62 134.941l16.766 64.534-15.297 3.974-16.766-64.534zM155.422 167.607l74.632 43.849-5.027 8.556-74.631-43.849 5.026-8.556ZM118.435 259.08l103.125-22.983 3.438 15.426-103.125 22.984zM255.341 342.053l1.756-70.058 9.92.249-1.756 70.058z"
                />
                <path
                  d="M283.625 239.882c-3.834 12.996-17.478 20.424-30.475 16.59-12.996-3.834-20.424-17.478-16.59-30.475 3.834-12.996 17.478-20.424 30.474-16.59 12.997 3.834 20.425 17.478 16.591 30.475Z"
                  fill="#FEF3F3"
                />
              </g>
              <defs>
                <filter
                  id="a"
                  x="12.192"
                  y="41.752"
                  width="461.398"
                  height="365.775"
                  filterUnits="userSpaceOnUse"
                  color-interpolation-filters="sRGB"
                >
                  <feFlood flood-opacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dy="4" />
                  <feGaussianBlur stdDeviation="2" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
                  <feBlend
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow_1704_145"
                  />
                  <feBlend
                    in="SourceGraphic"
                    in2="effect1_dropShadow_1704_145"
                    result="shape"
                  />
                </filter>
              </defs>
            </svg>
            <div className="landing-content-ctas">
              <div className="landing-content-main">
                {privateRepositoryPrompt && (
                  <div
                    style={{
                      backgroundColor: "#fff3cd",
                      border: "1px solid #ffeaa7",
                      borderRadius: "10px",
                      padding: "15px",
                      marginBottom: "20px",
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      style={{
                        color: "#856404",
                        fontWeight: "bold",
                        marginBottom: "5px",
                      }}
                    >
                      🔒 Private Repository Detected
                    </Typography>
                    <Typography style={{ color: "#856404", fontSize: "14px" }}>
                      This repository appears to be private. Please sign in with
                      GitHub to access it.
                    </Typography>
                  </div>
                )}
                <div className="landing-url-wrapper">
                  <div className="landing-button-main-icon-wrapper">
                    <LinkIcon />
                  </div>
                  <input
                    placeholder="Enter GitHub Repo URL"
                    className="landing-button landing-button-main landing-url-input"
                    onChange={handlePublicURLChange}
                    onKeyUp={handleEnterKey}
                    value={publicURL}
                    type="url"
                  ></input>
                </div>
                <button
                  id="local-project-button"
                  className="landing-button landing-button-main"
                  onClick={handleOpenFolder}
                >
                  <div className="landing-button-main-icon-wrapper">
                    <DriveFolderUpload />
                  </div>
                  <Typography color={theme.custom.pink}>
                    Open Project From Local Device
                  </Typography>
                </button>
                <button
                  className={`landing-button landing-button-main ${
                    privateRepositoryPrompt ? "private-repo-highlight" : ""
                  }`}
                  onClick={handleLogin}
                  style={
                    privateRepositoryPrompt
                      ? {
                          backgroundColor: "#ffaea6",
                          color: "white",
                          border: "2px solid #ff8080",
                          boxShadow: "0 0 10px rgba(255, 174, 166, 0.5)",
                          transform: "scale(1.02)",
                        }
                      : {}
                  }
                >
                  <div className="landing-button-main-icon-wrapper landing-button-icon-github">
                    <GitHub />
                  </div>
                  <Typography
                    color={
                      privateRepositoryPrompt ? "white" : theme.custom.pink
                    }
                  >
                    Open Project From GitHub
                  </Typography>
                </button>
              </div>
              <div className="landing-content-info">
                <button
                  className="landing-button landing-button-info"
                  onClick={handleShowVideo}
                >
                  <Typography color={theme.custom.pink}>
                    <div className="landing-button-info-icon-wrapper">
                      <PlayArrow className="landing-button-playicon" />
                    </div>
                    Start Here
                  </Typography>
                </button>
                <button
                  id="demo_button"
                  className="landing-button landing-button-info"
                  onClick={loadDemo}
                >
                  <Typography color={theme.custom.pink}>
                    <div className="landing-button-info-icon-wrapper">
                      <AccountTree />
                    </div>
                    Demo
                  </Typography>
                </button>
                <button
                  className="landing-button landing-button-info"
                  onClick={goNotion}
                >
                  <div className="landing-button-info-icon-wrapper">
                    <School />
                  </div>
                  <Typography color={theme.custom.pink}>Learn More</Typography>
                </button>
              </div>
            </div>
            <Typography
              style={{ fontSize: "13px", paddingTop: "20px" }}
              color={theme.custom.darkGrey}
            >
              By using Code Canvas, you agree to our{" "}
              <a
                href="https://pie-crepe-38f.notion.site/CodeCanvas-Terms-and-Conditions-ef6b3e7a3ee54da3b363c48112062bce?pvs=4"
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontWeight: "normal", color: theme.custom.pink }}
              >
                terms of service
              </a>{" "}
              and{" "}
              <a
                href="https://pie-crepe-38f.notion.site/CodeCanvas-Privacy-Policy-5466713f74724c9ab0da9a1d669ab379?pvs=4"
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontWeight: "normal", color: theme.custom.pink }}
              >
                privacy policy
              </a>
            </Typography>
          </div>
          <RecentFilesDropdown />
        </div>
      )}
      <VideoTutorial />
    </>
  );
};

export default LandingComponent;
